import { useQuery } from '@tanstack/react-query';
import { ArrowDownRight, ArrowUpRight, BadgeDollarSign, CheckCheck, Eye, Gift, Megaphone, OctagonX, UserX } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/misc';
import { apiFetch } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { QUALITY, limitLabel, metaLabel } from '@/lib/official-labels';
import { cn } from '@/lib/utils';

// Painel do WhatsApp oficial: quanto a META vai cobrar (o bZapper cobra só a
// mensalidade) e os indicadores que só existem neste trilho. Os números vêm de
// GET /official/insights; a estimativa usa a categoria que a Meta informa em
// cada status e, na falta, a do template.

type Category = 'marketing' | 'utility' | 'authentication' | 'service';

interface Insights {
  rate_card: { currency: string; effective: string; rates: Record<Category, number> };
  cost: {
    currency: string; period: number; previous_period: number; change_pct: number | null;
    month_to_date: number; projected_month: number;
    by_category: { category: Category; messages: number; billable: number; cost: number }[];
    confirmed: number; estimated: number; other_countries: number;
  };
  messages: {
    sent: number; delivered: number; read: number; failed: number; received: number;
    delivery_rate: number; read_rate: number; free_messages: number; contacts: number;
  };
  templates: { name: string; category: Category; sent: number; failed: number; cost: number }[];
  failures: { reason: string; count: number }[];
  ad_referrals: number;
  marketing_opt_outs: number;
  numbers: { display_number: string; quality: string; limit: string; status: string }[];
}

const CAT_COLOR: Record<Category, string> = {
  marketing: 'bg-rose-500', utility: 'bg-sky-500', authentication: 'bg-violet-500', service: 'bg-emerald-500',
};
const QUALITY_TONE: Record<string, 'success' | 'warning' | 'destructive' | 'secondary'> = {
  GREEN: 'success', YELLOW: 'warning', RED: 'destructive',
};

export function OfficialInsights({ days }: { days: number }) {
  const { t, locale } = useI18n();
  const { data, isLoading } = useQuery({
    queryKey: ['official-insights', days],
    queryFn: async (): Promise<Insights | null> => {
      const r = await apiFetch('GET', `/official/insights?days=${days}`);
      return r.ok ? (r.data as Insights) : null;
    },
    refetchInterval: 60_000,
  });
  if (isLoading) return <Skeleton className="h-64 w-full" />;
  if (!data) return null;

  const c = data.cost;
  const money = (v: number) => new Intl.NumberFormat(locale, { style: 'currency', currency: c.currency || 'BRL' }).format(v);
  const unit = (v: number) => new Intl.NumberFormat(locale, { style: 'currency', currency: c.currency || 'BRL', minimumFractionDigits: 4 }).format(v);
  const n = (v: number) => v.toLocaleString(locale);
  const maxCat = Math.max(1, ...c.by_category.map((x) => x.messages));
  const up = (c.change_pct ?? 0) > 0;

  const kpis = [
    { key: 'delivery', icon: CheckCheck, tone: 'text-amber-500', label: t('dashboard.official.deliveryRate'), value: `${data.messages.delivery_rate.toFixed(1)}%`, hint: t('dashboard.official.ofSent', { n: n(data.messages.sent) }) },
    { key: 'read', icon: Eye, tone: 'text-sky-500', label: t('dashboard.official.readRate'), value: `${data.messages.read_rate.toFixed(1)}%`, hint: t('dashboard.official.readHint', { n: n(data.messages.read) }) },
    { key: 'free', icon: Gift, tone: 'text-emerald-500', label: t('dashboard.official.free'), value: n(data.messages.free_messages), hint: t('dashboard.official.freeHint') },
    { key: 'ads', icon: Megaphone, tone: 'text-primary', label: t('dashboard.official.ads'), value: n(data.ad_referrals), hint: t('dashboard.official.adsHint') },
    { key: 'optout', icon: UserX, tone: 'text-rose-500', label: t('dashboard.official.optOuts'), value: n(data.marketing_opt_outs), hint: t('dashboard.official.optOutsHint') },
    { key: 'failed', icon: OctagonX, tone: 'text-destructive', label: t('dashboard.official.failed'), value: n(data.messages.failed), hint: t('dashboard.official.failedHint') },
  ];

  return (
    <div className="flex flex-col gap-6">
      {/* Custo com a Meta */}
      <Card>
        <CardHeader className="flex-row items-center gap-2 pb-2">
          <BadgeDollarSign className="size-5 text-primary" />
          <CardTitle>{t('dashboard.official.costTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          <div className="grid gap-4 sm:grid-cols-3 *:min-w-0">
            <div>
              <p className="text-xs text-muted-foreground">{t('dashboard.official.monthToDate')}</p>
              <p className="text-2xl font-bold tabular-nums">{money(c.month_to_date)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{t('dashboard.official.projected')}</p>
              <p className="text-2xl font-bold tabular-nums">{money(c.projected_month)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{t('dashboard.official.period', { n: days })}</p>
              <p className="flex items-baseline gap-2 text-2xl font-bold tabular-nums">
                {money(c.period)}
                {c.change_pct != null && (
                  <span className={cn('flex items-center text-sm font-medium', up ? 'text-rose-600' : 'text-emerald-600')}>
                    {up ? <ArrowUpRight className="size-4" /> : <ArrowDownRight className="size-4" />}{Math.abs(c.change_pct).toFixed(1)}%
                  </span>
                )}
              </p>
              <p className="text-xs text-muted-foreground">{t('dashboard.official.previous', { value: money(c.previous_period) })}</p>
            </div>
          </div>

          <div className="flex flex-col gap-2.5">
            {c.by_category.map((x) => (
              <div key={x.category} className="grid grid-cols-[8.5rem_1fr_auto] items-center gap-3 text-sm">
                <span className="flex items-center gap-2">
                  <span className={cn('size-2.5 rounded-full', CAT_COLOR[x.category])} />
                  {t(`dashboard.official.cat.${x.category}`)}
                </span>
                <div className="h-2 overflow-hidden rounded-full bg-muted">
                  <div className={cn('h-full rounded-full', CAT_COLOR[x.category])} style={{ width: `${(x.messages / maxCat) * 100}%` }} />
                </div>
                <span className="w-56 text-right tabular-nums text-muted-foreground">
                  {t('dashboard.official.catLine', { messages: n(x.messages), billable: n(x.billable) })}
                  {' · '}<span className="font-medium text-foreground">{x.category === 'service' ? t('dashboard.official.freeLabel') : money(x.cost)}</span>
                </span>
              </div>
            ))}
          </div>

          <p className="text-xs text-muted-foreground">
            {t('dashboard.official.costNote', {
              marketing: unit(data.rate_card.rates.marketing),
              utility: unit(data.rate_card.rates.utility),
              authentication: unit(data.rate_card.rates.authentication),
            })}
            {' '}{t('dashboard.official.basis', { confirmed: n(c.confirmed), estimated: n(c.estimated) })}
            {c.other_countries > 0 && <> {t('dashboard.official.otherCountries', { n: n(c.other_countries) })}</>}
          </p>
        </CardContent>
      </Card>

      {/* KPIs do oficial */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 *:min-w-0">
        {kpis.map((k) => (
          <Card key={k.key}>
            <CardHeader className="flex-row items-center justify-between pb-1">
              <CardTitle className="text-xs font-medium text-muted-foreground">{k.label}</CardTitle>
              <k.icon className={cn('size-4', k.tone)} />
            </CardHeader>
            <CardContent>
              <p className="text-xl font-bold tabular-nums">{k.value}</p>
              <p className="text-[11px] text-muted-foreground">{k.hint}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-3 *:min-w-0">
        {/* Templates */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2"><CardTitle>{t('dashboard.official.templatesTitle')}</CardTitle></CardHeader>
          <CardContent>
            {data.templates.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">{t('dashboard.official.noTemplates')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-muted-foreground">
                    <tr className="border-b">
                      <th className="py-2 text-left font-medium">{t('dashboard.official.colTemplate')}</th>
                      <th className="py-2 text-left font-medium">{t('dashboard.official.colCategory')}</th>
                      <th className="py-2 text-right font-medium">{t('dashboard.official.colSent')}</th>
                      <th className="py-2 text-right font-medium">{t('dashboard.official.colFailed')}</th>
                      <th className="py-2 text-right font-medium">{t('dashboard.official.colCost')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.templates.map((tp) => (
                      <tr key={tp.name + tp.category} className="border-b last:border-0">
                        <td className="py-2 font-mono text-xs">{tp.name}</td>
                        <td className="py-2"><span className="flex items-center gap-1.5"><span className={cn('size-2 rounded-full', CAT_COLOR[tp.category])} />{t(`dashboard.official.cat.${tp.category}`)}</span></td>
                        <td className="py-2 text-right tabular-nums">{n(tp.sent)}</td>
                        <td className={cn('py-2 text-right tabular-nums', tp.failed > 0 && 'text-destructive')}>{n(tp.failed)}</td>
                        <td className="py-2 text-right tabular-nums">{money(tp.cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Números e falhas */}
        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader className="pb-2"><CardTitle>{t('dashboard.official.numbersTitle')}</CardTitle></CardHeader>
            <CardContent className="flex flex-col gap-2">
              {data.numbers.map((nu) => (
                <div key={nu.display_number} className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-mono text-xs">{nu.display_number || '—'}</span>
                  {nu.quality && <Badge variant={QUALITY_TONE[nu.quality] ?? 'secondary'}>{metaLabel(t, QUALITY, 'official.qualityValue', nu.quality)}</Badge>}
                  <span className="text-xs text-muted-foreground">{limitLabel(t, nu.limit)}</span>
                </div>
              ))}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle>{t('dashboard.official.failuresTitle')}</CardTitle></CardHeader>
            <CardContent className="flex flex-col gap-2">
              {data.failures.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('dashboard.official.noFailures')}</p>
              ) : data.failures.map((f) => (
                <div key={f.reason} className="flex items-start justify-between gap-3 text-xs">
                  <span className="text-muted-foreground">{f.reason}</span>
                  <span className="font-semibold tabular-nums text-destructive">{n(f.count)}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
